import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { BosfedLogoName, BosfedName, BostonFederalName, ScapeSourceName } from '../components/RegisteredName';

const values = [
  { num: '01', title: 'Precision', desc: 'Disciplined execution at every layer of financial operations. We bring the rigor of enterprise finance to businesses of all sizes.' },
  { num: '02', title: 'Transparency', desc: 'Clear reporting, full visibility, and honest communication. Financial clarity is the foundation of confident decision-making.' },
  { num: '03', title: 'Systems Thinking', desc: 'An infrastructure-first approach to finance. We design processes and systems that scale, adapt, and deliver consistent results.' },
  { num: '04', title: 'Accountability', desc: 'We take ownership of outcomes, not just deliverables. Your financial health is our shared responsibility.' },
  { num: '05', title: 'Innovation', desc: 'Building the tools and systems of tomorrow, today. We continuously invest in proprietary technology to serve clients better.' },
  { num: '06', title: 'Partnership', desc: 'We are your financial team, not just a vendor. We integrate deeply with your business to drive real, lasting results.' },
];

const services = [
  'Financial Control & Visibility', 'Strategic Financial Planning',
  'Financial Infrastructure Design', 'Ongoing Accounting Operations',
  'Reporting & Data Visibility', 'Automation & Intelligent Operations',
  'Payroll & Workforce Compliance', 'Bankability & Capital Readiness',
];

const philosophy = [
  { step: '01', title: 'Discover', desc: 'We start by understanding your business deeply — your operations, challenges, compliance obligations, and growth goals. No assumptions.' },
  { step: '02', title: 'Build', desc: 'We design and implement the right financial infrastructure for your stage — from foundational controls to advanced automation and reporting.' },
  { step: '03', title: 'Operate', desc: 'We run your financial operations with precision and consistency — close cycles, reconciliations, reporting, and compliance handled every month.' },
  { step: '04', title: 'Evolve', desc: 'As your business grows, we evolve your systems and processes with you — adding capability, automation, and intelligence at every stage.' },
];

const tabs = ['Mission', 'Values', 'How We Work'];

export default function About() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div>
      {/* ── Hero ─────────────────────────────────── */}
      <section className="grid-bg pt-32 pb-14" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-[#1FA8F5] text-[10px] font-bold uppercase tracking-[0.25em] mb-4">About <BostonFederalName /></p>
            <h1 className="text-5xl sm:text-6xl font-bold text-[#0B1F33] leading-[1.05] tracking-tight mb-5 max-w-3xl">
              Disciplined Finance.<br />Modern Systems.<br /><span className="text-[#1FA8F5]">Built for What's Next.</span>
            </h1>
            <p className="text-lg text-[#40516A] max-w-2xl leading-relaxed">
              <BostonFederalName /> is an innovative, hands-on financial control and infrastructure firm helping businesses gain visibility, improve compliance, automate operations, and build the foundation needed to grow.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Tab Section ──────────────────────────── */}
      <section className="bg-white pb-20" data-testid="about-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Tab Bar */}
          <div>
            <div className="flex overflow-x-auto no-scrollbar">
              {tabs.map((tab, i) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(i)}
                  data-testid={`about-tab-${i}`}
                  className={`px-6 py-4 text-sm font-semibold whitespace-nowrap transition-colors duration-200 ${
                    i === activeTab ? 'text-[#1FA8F5]' : 'text-[#40516A] hover:text-[#0B1F33]'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28 }}
            >
              {/* Mission tab */}
              {activeTab === 0 && (
                <div className="py-14 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                  <div>
                    <p className="text-[#1FA8F5] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">Our Mission</p>
                    <h2 className="text-3xl sm:text-4xl font-bold text-[#0B1F33] leading-tight tracking-tight mb-6">
                      Reimagining Financial Control for the Next Generation
                    </h2>
                    <p className="text-[#40516A] leading-relaxed mb-4">
                      We combine the discipline of traditional accounting with modern systems, automation, and intelligent reporting to create a new standard of financial control — one that makes businesses faster, smarter, and more resilient.
                    </p>
                    <p className="text-[#40516A] leading-relaxed mb-4">
                      Our approach is hands-on and execution-oriented. We don't just advise — we build, operate, and continuously improve financial infrastructure alongside our clients.
                    </p>
                    <p className="text-[#40516A] leading-relaxed mb-8">
                      Through <BosfedName /> and our innovation arm <ScapeSourceName />, we are building the next generation of financial control tools — proprietary technology designed to give businesses the visibility, compliance, and operational efficiency they need to thrive.
                    </p>
                    <Link to="/capabilities" data-testid="about-explore-capabilities" className="bg-[#1FA8F5] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#0B8ED8] transition-colors inline-flex items-center gap-2">
                      Explore Our Capabilities <ArrowRight size={17} />
                    </Link>
                  </div>
                  <div className="bg-[#F7FAFC] p-8">
                    <div className="logo-text text-[#0B1F33] text-2xl tracking-[0.16em] mb-7"><BosfedLogoName /></div>
                    <div className="space-y-3.5">
                      {services.map(s => (
                        <div key={s} className="flex items-center">
                          <span className="text-[#40516A] text-sm">{s}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Values tab */}
              {activeTab === 1 && (
                <div className="py-14">
                  <p className="text-[#1FA8F5] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">What We Stand For</p>
                  <h2 className="text-3xl sm:text-4xl font-bold text-[#0B1F33] leading-tight tracking-tight mb-10">
                    The Principles That Guide Us
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
                    {values.map((v, i) => (
                      <motion.div
                        key={v.num}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.07, duration: 0.35 }}
                        data-testid={`value-${v.num}`}
                      >
                        <span className="text-[#1FA8F5] font-bold text-3xl">{v.num}</span>
                        <h3 className="text-[#0B1F33] font-bold text-lg mt-2 mb-2">{v.title}</h3>
                        <p className="text-[#40516A] text-sm leading-relaxed">{v.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* How We Work tab */}
              {activeTab === 2 && (
                <div className="py-14">
                  <p className="text-[#1FA8F5] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">Our Approach</p>
                  <h2 className="text-3xl sm:text-4xl font-bold text-[#0B1F33] leading-tight tracking-tight mb-3">
                    How <BostonFederalName /> Works
                  </h2>
                  <p className="text-[#40516A] max-w-2xl leading-relaxed mb-12">
                    We take a structured, execution-oriented approach to every engagement — starting with a deep understanding of your business and building from there.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                    {philosophy.map((p, i) => (
                      <motion.div
                        key={p.step}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1, duration: 0.35 }}
                      >
                        <div className="w-10 h-10 flex items-center justify-center mb-4">
                          <span className="text-[#1FA8F5] font-bold text-sm">{p.step}</span>
                        </div>
                        <h3 className="text-[#0B1F33] font-bold text-lg mb-2">{p.title}</h3>
                        <p className="text-[#40516A] text-sm leading-relaxed">{p.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── Dark CTA ──────────────────────────────── */}
      <section className="dark-grid-bg py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <h2 className="text-4xl font-bold text-white mb-4">Partner with <BostonFederalName /></h2>
            <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">Let's build the financial infrastructure your business needs to grow.</p>
            <Link to="/contact" data-testid="about-cta-contact" className="bg-[#1FA8F5] text-white px-8 py-3.5 rounded-md font-semibold hover:bg-[#0B8ED8] transition-colors inline-flex items-center gap-2">
              Get in Touch <ArrowRight size={17} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
